import React, { Component, ReactNode, SyntheticEvent } from 'react';
import classNames from 'classnames/bind';
import Button, { BUTTON_TYPES } from 'components/Button/Button';
import { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import IconButton from 'components/IconButton/IconButton';
import String from 'components/String/String';
import Space from 'corecomponents/Space/Space';
import PropTypes from 'prop-types';
import styles from './FormLayout.module.css';

// The type argument is what lets the provider below pass `value` (i.e. `true`);
// inferred from the argument alone this context is `undefined` and nothing else.
// The default stays `undefined` so consumers outside a provider are unchanged.
export const FormLayoutContext = React.createContext<boolean | undefined>(
  undefined,
);

const cx = classNames.bind(styles);

/**
 * Scrolls to the first form error element on mobile devices
 */
function scrollToFirstError(): void {
  // Only run on mobile devices
  if (window.innerWidth >= 768) return;

  // Find the first error element using a more generic selector
  const firstErrorElement = document.querySelector('[class*="error"]');

  if (firstErrorElement) {
    // Calculate position to scroll to
    const elementRect = firstErrorElement.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;

    // Use fixed offset from top to ensure visibility
    const scrollPosition = absoluteElementTop - 150;

    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  }
}

const renderString = (node: ReactNode) => {
  if (typeof node === 'string') {
    return <String string={node} />;
  }
  return node;
};

interface TitleProps {
  // `PropTypes.oneOfType([string, node]).isRequired` infers as
  // `NonNullable<ReactNodeLike>`, which a JSX element is not assignable to, so
  // both `Section` call sites below were errors.
  title: ReactNode;
  header?: boolean;
  className?: string;
}

const Title = ({ title, header, className = '' }: TitleProps) => (
  <div className={cx(header ? 'title' : 'section-title', className)}>
    {renderString(title)}
  </div>
);

Title.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  header: PropTypes.bool,
  className: PropTypes.string,
};

Title.defaultProps = {
  header: false,
  className: '',
};

interface FormLayoutHeaderProps {
  // `renderString` below wraps plain strings in `String` and returns anything
  // else untouched, so these have always accepted markup as well as copy.
  subtitle?: ReactNode;
  pretitle?: ReactNode;
  title?: string;
}

interface SectionProps {
  firstSection?: boolean;
  sectionTitle?: string | ReactNode;
  sectionTitleClassName?: string;
  sectionSubTitle?: string | ReactNode;
  separatorType?: string;
  children?: ReactNode;
  disableTitle?: boolean;
  large?: boolean;
  className?: string;
}

interface ButtonProps {
  buttonClassNames?: string;
  nextButtonWrapperProps?: Record<string, unknown>;
  // `any` rather than `unknown`: `Button` handles its own props, and `handleClick`
  // below calls `ctaProps.onClick`, which an `unknown` value is not callable as.
  ctaProps?: Record<string, any>;
  className?: string;
}

interface BottomBarProps {
  ctaProps?: Record<string, any>;
  previousStep?: string | null;
  previousStepCustomLabel?: string | null;
  previousStepProps?: Record<string, unknown>;
  skip?: (() => void) | null;
  callout?: ReactNode;
  nextButtonWrapperProps?: Record<string, unknown>;
  customClassName?: string;
  previousContent?: ReactNode;
}

interface DividerProps {
  label?: string;
}

interface FormLayoutProps {
  children: ReactNode;
}

class FormLayout extends Component<FormLayoutProps> {
  static Title = Title;

  static FormLayoutHeader = ({
    subtitle,
    pretitle,
    title,
  }: FormLayoutHeaderProps) => {
    const getTitle = () => {
      if (subtitle && title) {
        return (
          <div className={cx('form-header-wrapper')}>
            <Title header title={title} />
            <Space value="xs" />
            <div className={cx('subtitle')}>{renderString(subtitle)}</div>
          </div>
        );
      }

      if (pretitle && title) {
        return (
          <div className={cx('form-header-wrapper')}>
            <div className={cx('pretitle')}>{renderString(pretitle)}</div>
            <Space value="xs" />
            <Title header title={title} />
          </div>
        );
      }
      if (subtitle) {
        return (
          <div className={cx('form-header-wrapper')}>
            <div className={cx('subtitle')}>{renderString(subtitle)}</div>
          </div>
        );
      }
      if (title) {
        return (
          <div className={cx('form-header-wrapper')}>
            <Title header title={title} />
          </div>
        );
      }
      return null;
    };
    return getTitle();
  };

  static Section = ({
    firstSection,
    sectionTitle,
    sectionTitleClassName,
    sectionSubTitle,
    separatorType,
    children,
    disableTitle,
    large,
    className,
  }: SectionProps) => {
    const getSectionTitle = () => {
      if (sectionTitle && sectionSubTitle) {
        return (
          <div
            className={cx(
              'section-title-wrapper',
              { firstSection },
              { disableTitle },
            )}
          >
            <Title title={sectionTitle} className={sectionTitleClassName} />
            <Space value="xs" />
            <div className={cx('section-subtitle')}>
              {renderString(sectionSubTitle)}
            </div>
          </div>
        );
      }
      if (sectionSubTitle) {
        return (
          <div
            className={cx(
              'section-title-wrapper',
              { firstSection },
              { disableTitle },
            )}
          >
            <div className={cx('section-subtitle')}>
              {renderString(sectionSubTitle)}
            </div>
          </div>
        );
      }
      if (sectionTitle) {
        return (
          <div
            className={cx(
              'section-title-wrapper',
              { firstSection },
              { disableTitle },
            )}
          >
            <Title title={sectionTitle} className={sectionTitleClassName} />
          </div>
        );
      }
      return null;
    };
    return (
      <div
        className={cx('section-wrapper', separatorType, { large }, className)}
      >
        {getSectionTitle()}
        <div className={cx('section')}>{children}</div>
      </div>
    );
  };

  static BottomBar = ({
    ctaProps,
    previousStep,
    previousStepCustomLabel,
    previousStepProps = {},
    skip,
    callout,
    nextButtonWrapperProps = {},
    customClassName = '',
    previousContent,
  }: BottomBarProps) => (
    <>
      <div className={cx(['bottom-bar', customClassName])}>
        <div className={cx('previous')}>
          {previousContent}
          {(previousStep || !!Object.keys(previousStepProps).length) && (
            <IconButton
              icon={GENERAL_ICONS.BACK_ARROW}
              label={previousStepCustomLabel || 'Previous'}
              to={previousStep}
              textstyle="uppercase"
              {...previousStepProps}
            />
          )}
        </div>
        <div className={cx('next')}>
          {callout && <div className={cx('callout')}>{callout}</div>}
          {skip && (
            <Button
              className={cx('skip')}
              buttonType={BUTTON_TYPES.TEXT}
              label="Skip"
              onClick={skip}
            />
          )}
          <FormLayout.Button
            buttonClassNames={cx('button-container')}
            nextButtonWrapperProps={nextButtonWrapperProps}
            ctaProps={ctaProps}
          />
        </div>
      </div>

      <div className={cx('bottom-bar-mobile-wrapper')}>
        <div className={cx('bottom-bar-margin')} />
        <div className={cx('bottom-bar-mobile', 'safari-inset')}>
          {callout && <div className={cx('callout')}>{callout}</div>}
          {skip && (
            <Button
              className={cx('skip')}
              buttonType={BUTTON_TYPES.TEXT}
              label="Skip"
              onClick={skip}
            />
          )}
          <div className={cx('main-ctas')}>
            <FormLayout.Button
              buttonClassNames={cx('button-container', 'withoutPrevious')}
              nextButtonWrapperProps={nextButtonWrapperProps}
              ctaProps={ctaProps}
            />
          </div>
        </div>
      </div>
    </>
  );

  static Button = ({
    buttonClassNames,
    nextButtonWrapperProps,
    ctaProps,
  }: ButtonProps) => {
    const handleClick = (e: SyntheticEvent) => {
      // Add small timeout to let validation errors render first
      setTimeout(() => scrollToFirstError(), 100);

      // Call original onClick if it exists
      if (ctaProps && ctaProps.onClick) {
        ctaProps.onClick(e);
      }
    };

    const composedCtaProps = {
      ...ctaProps,
      onClick: handleClick,
    };

    return (
      <div className={buttonClassNames} {...nextButtonWrapperProps}>
        <Button
          className={cx('cta-button')}
          type="submit"
          size="fit"
          {...composedCtaProps}
        />
      </div>
    );
  };

  // This should pass in the route to the previous page
  // and the Previous Step Icon should be here

  static Divider = ({ label }: DividerProps) => (
    <div className={cx('divider', { label })}>
      <span className={cx('divider-left-line')} />
      {label && <span>{label}</span>}
      <span className={cx('divider-right-line')} />
    </div>
  );

  static propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
  };

  render() {
    const { children } = this.props;
    return (
      <FormLayoutContext.Provider value>
        <div className={cx('form-layout')}>{children}</div>
      </FormLayoutContext.Provider>
    );
  }
}

export default FormLayout;
